'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MapPin, Phone, MessageSquare, Send, Loader2, CheckCircle } from 'lucide-react';

interface ContactFormData {
  name: string;
  phone: string;
  email: string;
  message: string;
}

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>();

  // BACKEND INTEGRATION: Replace with POST /api/contact to store and email the enquiry
  const onSubmit = async (data: ContactFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    console.log('Contact form data:', data);
    setSubmitted(true);
    setIsLoading(false);
    reset();
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <section id="contact" className="py-20 px-6 lg:px-10 section-bg">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-bold text-primary uppercase tracking-widest mb-3 block">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
            Contact <span className="gradient-blue-text">Navya Travels</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Reach out for bookings, enquiries, or custom tour packages. We respond within minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left: Info */}
          <div className="space-y-6">
            {/* Address */}
            <div className="glass-card border border-border/20 rounded-2xl p-6 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin size={22} className="text-primary" />
              </div>
              <div>
                <h4 className="font-bold text-white mb-1">Our Location</h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Shalimar Bagh, Delhi, India – 110088
                  <br />
                  <span className="text-xs text-muted-foreground/70">Available 24/7 for bookings</span>
                </p>
              </div>
            </div>

            {/* Phone Numbers */}
            <div className="glass-card border border-border/20 rounded-2xl p-6 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Phone size={22} className="text-primary" />
              </div>
              <div>
                <h4 className="font-bold text-white mb-2">Call / WhatsApp</h4>
                <div className="space-y-2">
                  <a
                    href="tel:+918860044910"
                    className="flex items-center gap-2 text-sm text-slate-300 hover:text-primary transition-colors"
                  >
                    📞 <span className="font-semibold">+91 88600 44910</span>
                    <span className="text-xs text-muted-foreground">(Primary)</span>
                  </a>
                  <a
                    href="tel:+918448729015"
                    className="flex items-center gap-2 text-sm text-slate-300 hover:text-primary transition-colors"
                  >
                    📞 <span className="font-semibold">+91 84487 29015</span>
                    <span className="text-xs text-muted-foreground">(Alternate)</span>
                  </a>
                </div>
              </div>
            </div>

            {/* WhatsApp Quick Actions */}
            <div className="glass-card border border-border/20 rounded-2xl p-6 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center shrink-0">
                <MessageSquare size={22} className="text-green-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-white mb-2">Quick WhatsApp</h4>
                <div className="flex flex-col gap-2">
                  <a
                    href="https://wa.me/918860044910?text=Hi%20Navya%20Travels!%20I%20want%20to%20book%20a%20Delhi%20cab."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-green-500/10 border border-green-500/20 text-green-400 px-3 py-2 rounded-lg hover:bg-green-500/20 transition-colors font-medium"
                  >
                    🚖 Book Delhi Cab — +91 88600 44910
                  </a>
                  <a
                    href="https://wa.me/918860044910?text=Hi%20Navya%20Travels!%20I%20need%20an%20outstation%20tour%20package."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-green-500/10 border border-green-500/20 text-green-400 px-3 py-2 rounded-lg hover:bg-green-500/20 transition-colors font-medium"
                  >
                    🗺️ Outstation Enquiry — +91 88600 44910
                  </a>
                </div>
              </div>
            </div>

            {/* Map Embed */}
            <div className="glass-card border border-border/20 rounded-2xl overflow-hidden h-48">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3499.5270068849!2d77.16!3d28.72!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d02d8c7e2b5c3%3A0x0!2sShalimar+Bagh%2C+Delhi%2C+India!5e0!3m2!1sen!2sin!4v1"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Navya Travels location in Shalimar Bagh Delhi"
              />
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="glass-card border border-border/20 rounded-2xl p-6 lg:p-8">
            <h3 className="text-xl font-bold text-white mb-6">Send an Enquiry</h3>

            {submitted ? (
              <div className="text-center py-12">
                <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
                <h4 className="text-lg font-bold text-white mb-2">Message Sent!</h4>
                <p className="text-muted-foreground text-sm">
                  We will contact you within 15 minutes. For urgent bookings, call us directly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-1.5">Full Name</label>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    placeholder="Your full name"
                    className="input-dark w-full px-4 py-3 text-sm"
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: { value: /^[6-9]\d{9}$/, message: 'Enter valid 10-digit number' },
                    })}
                    placeholder="10-digit mobile number"
                    className="input-dark w-full px-4 py-3 text-sm"
                    maxLength={10}
                  />
                  {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-1.5">
                    Email Address <span className="text-muted-foreground font-normal">(Optional)</span>
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    placeholder="your@email.com"
                    className="input-dark w-full px-4 py-3 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white mb-1.5">Message / Requirement</label>
                  <textarea
                    {...register('message', { required: 'Message is required' })}
                    rows={4}
                    placeholder="Tell us about your trip — destination, dates, number of passengers..."
                    className="input-dark w-full px-4 py-3 text-sm resize-none"
                  />
                  {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-4 rounded-xl font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isLoading ? (
                    <><Loader2 size={18} className="animate-spin" /> Sending...</>
                  ) : (
                    <><Send size={18} /> Send Enquiry</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}